import {
    requestEntityAssetsList,
    removeSingleAsset,
    updateEntityAssetsMetadata,
    rotateEntityImage,
    uploadSingleFile,
    uploadSingleMedia,
} from '../util/api';
import config from '../config';
import {
    ASSET_TYPE_FILE,
    ASSET_TYPE_MEDIA,
    GALLERY_FILES,
    GALLERY_IMAGES,
} from '../marketplace-custom-config';
import { updateUserProfileInfo } from './user.duck';
import {
    ERROR_CODE_INVALID_TYPE_VIDEO,
    ERROR_CODE_INVALID_TYPE_IMAGE,
    ERROR_CODE_SIZE_EXCEEDED_VIDEO,
    ERROR_CODE_SIZE_EXCEEDED_IMAGE,
} from '../util/types';
const { userTypeRider } = config;

export const ADD_ASSETS_LOADING_REQUEST = 'app/Assets/ADD_ASSETS_LOADING_REQUEST';
export const ADD_ASSETS_LOADING_SUCCESS = 'app/Assets/ADD_ASSETS_LOADING_SUCCESS';
export const ADD_ASSETS_DATA = 'app/Assets/ADD_ASSETS_DATA';
export const ADD_ASSETS_ERROR_DATA = 'app/Assets/ADD_ASSETS_ERROR_DATA';
export const REMOVE_SINGLE_ASSETS_DATA = 'app/Assets/REMOVE_SINGLE_ASSETS_DATA';
export const UPDATE_ASSETS_METADATA = 'app/Assets/UPDATE_ASSETS_METADATA';
export const DISCARD_ASSETS_ERROR_DATA = 'app/Assets/DISCARD_ASSETS_ERROR_DATA';

// ================ State ================ //

const initialState = {
    assetsLoadingRequests: {}, // user-listing ids
    assetsData: {}, // [user-or-listing-id]: { [variant]: [asset-data], [variant]: [asset-data], }
    assetsErrors: {}, // [user-or-listing-id]: error-data
};

export const checkResponseError = (data, message = 'Failed to list') => {
    if (data.error || (data.message && data.status === false)) {
        const errorText = data.message || (typeof data.error === 'string' ? data.error : message);
        const error = new Error(errorText);

        error.status = data.status;
        error.code = data.code;

        throw error;
    }
};

export const DEFAULT_VARIANT = 'entityAssets';
export const SQUARE_SMALL_2X = 'squareSmall2x';
export const VIDEO_THUMBNAIL = 'video_thumbnail';
export const ANONYM_ASSETS = 'anonym_assets';

// ================ Reducer ================ //

export const deleteStateEntity = (state, fieldName, entityId) => {
    const entity = { ...state[fieldName] };
    delete entity[entityId];
    return entity;
};

export const insertStateEntity = (state, fieldName, entityId, entityData) => ({
    ...state[fieldName],
    [entityId]: entityData,
});

export default function reducer(state = initialState, action = {}) {
    const { type, payload } = action;
    const { id, originalname, data, variant, code } = payload || {};

    switch (type) {
        case ADD_ASSETS_LOADING_REQUEST:
            return {
                ...state,
                assetsLoadingRequests: insertStateEntity(state, 'assetsLoadingRequests', id, true),
                assetsErrors: deleteStateEntity(state, 'assetsErrors', id),
            };
        case ADD_ASSETS_LOADING_SUCCESS:
            return {
                ...state,
                assetsLoadingRequests: deleteStateEntity(state, 'assetsLoadingRequests', id),
                assetsErrors: deleteStateEntity(state, 'assetsErrors', id),
            };
        case ADD_ASSETS_DATA:
            const listingAssets = state.assetsData[id];
            return {
                ...state,
                assetsData: insertStateEntity(state, 'assetsData', id, {
                    ...(listingAssets || {}),
                    [variant]: data,
                }),
                assetsErrors: deleteStateEntity(state, 'assetsErrors', id),
                assetsLoadingRequests: deleteStateEntity(state, 'assetsLoadingRequests', id),
            };
        case ADD_ASSETS_ERROR_DATA:
            return {
                ...state,
                assetsLoadingRequests: deleteStateEntity(state, 'assetsLoadingRequests', id),
                assetsErrors: insertStateEntity(state, 'assetsErrors', id, { code, data, id }),
            };
        case DISCARD_ASSETS_ERROR_DATA:
            return {
                ...state,
                assetsErrors: deleteStateEntity(state, 'assetsErrors', id),
            };
        case REMOVE_SINGLE_ASSETS_DATA:
            const fieldWithEntryRemoved = Object.entries(state.assetsData[id]).reduce(
                (acc, [variant, assets]) => ({
                    ...acc,
                    [variant]: (assets || []).filter(({ Key }) => !Key.includes(originalname)),
                }),
                {}
            );

            return {
                ...state,
                assetsData: {
                    ...state.assetsData,
                    [id]: fieldWithEntryRemoved,
                },
                assetsErrors: deleteStateEntity(state, 'assetsErrors', id),
                assetsLoadingRequests: deleteStateEntity(state, 'assetsLoadingRequests', id),
            };

        case UPDATE_ASSETS_METADATA:
            const fieldWithMetadataUpdated = Object.entries(state.assetsData[id]).reduce(
                (acc, [variant, assets]) => ({
                    ...acc,
                    [variant]: (assets || []).map(asset =>
                        asset.Key.includes(originalname) ? { ...asset, metadata: data } : asset
                    ),
                }),
                {}
            );

            return {
                ...state,
                assetsData: {
                    ...state.assetsData,
                    [id]: fieldWithMetadataUpdated,
                },
            };

        default:
            return state;
    }
}

// ================ Action creators ================ //

export const assetsRequest = payload => ({ type: ADD_ASSETS_LOADING_REQUEST, payload });
export const assetsRequestSuccess = payload => ({ type: ADD_ASSETS_LOADING_SUCCESS, payload });
export const assetsSuccess = payload => ({ type: ADD_ASSETS_DATA, payload });
export const assetsFailed = payload => ({ type: ADD_ASSETS_ERROR_DATA, payload });

export const removeAssetsSingle = payload => ({ type: REMOVE_SINGLE_ASSETS_DATA, payload });
export const changeDocumentMetadata = payload => ({ type: UPDATE_ASSETS_METADATA, payload });
export const discardAssetsError = payload => ({ type: DISCARD_ASSETS_ERROR_DATA, payload });

// ================ Thunks ================ //
const MIME_TO_EXT = {
    'image/svg+xml': 'svg',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
};
/**
 *
 * @param {String} type - asset - image & video only; common - image, video + pdf, docx etc formats
 * @returns
 */
export const uploadEntityAssets = ({
    file,
    metadata,
    type = ASSET_TYPE_FILE,
    entityType,
    id,
    rotateAngle = 0,
    addPreview = true,
}) => async dispatch => {
    dispatch(assetsRequest({ id }));

    const isMedia = type === ASSET_TYPE_MEDIA;

    if (isMedia) {
        const mimeType = file.type;
        const fileSizeInBytes = file.size;

        const fileExtension = MIME_TO_EXT[file.type] || '';
        const fileSizeInMB = fileSizeInBytes / (1024 * 1024);

        const isVideo = mimeType.startsWith('video/');
        const isImage = mimeType.startsWith('image/');

        let errorCode = null;

        if (isVideo) {
            const allowedVideoExtensions = [
                MIME_TO_EXT['video/mp4'],
                MIME_TO_EXT['video/quicktime'],
            ];
            const maxVideoSizeMB = 250;

            if (!allowedVideoExtensions.includes(fileExtension)) {
                errorCode = ERROR_CODE_INVALID_TYPE_VIDEO;
            }

            if (fileSizeInMB > maxVideoSizeMB) {
                errorCode = ERROR_CODE_SIZE_EXCEEDED_VIDEO;
            }
        } else if (isImage) {
            const allowedImageExtensions = [MIME_TO_EXT['image/jpeg'], MIME_TO_EXT['image/png']];
            const maxImageSizeMB = 50;

            if (!allowedImageExtensions.includes(fileExtension)) {
                errorCode = ERROR_CODE_INVALID_TYPE_IMAGE;
            }

            if (fileSizeInMB > maxImageSizeMB) {
                errorCode = ERROR_CODE_SIZE_EXCEEDED_IMAGE;
            }
        }

        if (errorCode) {
            return dispatch(
                assetsFailed({
                    id,
                    code: errorCode,
                    data: `Failed to upload assets for entity ${id}`,
                })
            );
        }
    }

    const params = {
        entityId: id,
        entityType,
        metadata,
        addPreview,
        rotateAngle,
    };

    const formData = new FormData();

    formData.append('file', file);
    formData.append('content', JSON.stringify(params));

    const apiEndpoint = isMedia ? uploadSingleMedia : uploadSingleFile;

    try {
        const response = await apiEndpoint(formData);

        checkResponseError(response, 'Failed to upload');
        /**
         * check if assetsRequest might be used in this case;
         * if this is the case - remove this thunk
         */
        dispatch(assetsRequestSuccess({ id }));

        return response; // isSuccessful
    } catch (e) {
        dispatch(
            assetsFailed({
                id,
                code: e.code,
                data: e.message || `Failed to upload assets for entity ${id}`,
            })
        );
    }
};

export const removeEntityAssets = ({ entityId, idToDelete }) => async dispatch => {
    dispatch(assetsRequest({ id: entityId }));

    try {
        const body = JSON.stringify({ Key: idToDelete });
        const response = await removeSingleAsset(body);

        checkResponseError(response, 'Failed to remove');
        dispatch(removeAssetsSingle({ id: entityId, originalname: response.originalname }));

        return response;
    } catch (e) {
        dispatch(
            assetsFailed({
                id: entityId,
                code: e.code,
                data: e.message || `Failed to upload assets for entity ${entityId}`,
            })
        );
    }
};

export const getEntityAssets = ({
    id,
    variant = DEFAULT_VARIANT,
    useCachedResults = false,
}) => async (dispatch, getState, sdk) => {
    const { assetsData } = getState().Assets;
    /**
     * requestInProgress causes infinite loading for EditListingPage
     * TODO: investigate how to avoid it
     *
     * const requestInProgress = assetsLoadingRequests && assetsLoadingRequests[id];
     * if (requestInProgress) return;
     */

    if (useCachedResults && assetsData && assetsData[id] && assetsData[id][variant]) {
        return assetsData[id];
    }

    dispatch(assetsRequest({ id }));

    try {
        const response = await requestEntityAssetsList(id, variant);
        const data = await response.json();

        checkResponseError(data);
        dispatch(assetsSuccess({ id, data, variant }));

        return data;
    } catch (e) {
        dispatch(
            assetsFailed({
                id,
                code: e.code,
                data: e.message || `Failed to receive assets for entity ${id}`,
            })
        );
    }
};

export const requestToRotateEntityImage = ({ idToUpdate, id, ...rest }) => async dispatch => {
    dispatch(assetsRequest({ id }));

    try {
        const body = JSON.stringify({ Key: idToUpdate, ...rest });
        const response = await rotateEntityImage(body);

        checkResponseError(response, 'Failed to update binary.');

        return response;
    } catch (e) {
        dispatch(
            assetsFailed({
                id,
                code: e.code,
                data: e.message || `Failed to alter assets' binary for entity ${id}`,
            })
        );
    }
};

export const changeEntityAssetMetadata = ({ idToUpdate, id, metadata }) => async dispatch => {
    dispatch(assetsRequest({ id }));

    try {
        const body = JSON.stringify({ Key: idToUpdate, metadata });
        const response = await updateEntityAssetsMetadata(body);

        checkResponseError(response, 'Failed to update metadata.');

        dispatch(
            changeDocumentMetadata({ originalname: response.originalname, id, data: metadata })
        );

        return response;
    } catch (e) {
        dispatch(
            assetsFailed({
                code: e.code,
                id,
                data: e.message || `Failed to update assets' metadata for entity ${id}`,
            })
        );
    }
};

export const uploadUserAvatarToGallery = ({ file, isDefaultAvatar }) => async (
    dispatch,
    getState
) => {
    const { currentUser } = getState().user;
    const { userType } = currentUser.attributes.profile.publicData;

    const isRider = userType === userTypeRider;

    try {
        if (isRider) {
            const id = currentUser.id.uuid;
            const response = await requestEntityAssetsList(id, SQUARE_SMALL_2X);
            const data = await response.json();

            const params = {
                id,
                file,
                type: ASSET_TYPE_MEDIA,
                entityType: 'user',
                metadata: {
                    position: String(data.length + 1),
                    type: GALLERY_FILES,
                    gallerytype: GALLERY_IMAGES,
                    description: '',
                },
            };

            if (isDefaultAvatar) {
                params.metadata.isdefaultavatar = 'true';
            }

            await dispatch(uploadEntityAssets(params));
            await dispatch(getEntityAssets({ id, variant: DEFAULT_VARIANT }));
            dispatch(
                updateUserProfileInfo({
                    publicData: { hasGalleryAssets: true },
                })
            );
        }
    } catch (e) {
        // todo
    }
};
